'use client'

import React, { useState } from 'react'
import Image from 'next/image'

export function StayConnectedSection() {
  const [email, setEmail] = useState('')
  const [category, setCategory] = useState('seeking care')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    // Placeholder - no actual backend submission
    setSubmitted(true)
    // Reset after 3 seconds
    setTimeout(() => {
      setSubmitted(false)
      setEmail('')
    }, 3000)
  }

  return (
    <section
      data-component-name="StayConnectedSection"
      style={{
        position: 'relative',
        padding: '120px 0',
        overflow: 'hidden',
      }}
    >
      {/* Background Image */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        <Image
          src="/images/stay-connected-bg.jpg"
          alt=""
          fill
          style={{
            objectFit: 'cover',
          }}
        />
        {/* Overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(245, 240, 235, 0.95)',
          }}
        />
      </div>

      {/* Decorative circle */}
      <div
        style={{
          position: 'absolute',
          right: '10%',
          top: '20%',
          width: '300px',
          height: '300px',
          zIndex: 1,
          pointerEvents: 'none',
          opacity: 0.3,
        }}
      >
        <Image
          src="/images/ellipse.svg"
          alt=""
          width={300}
          height={300}
          style={{
            width: '100%',
            height: 'auto',
          }}
        />
      </div>

      {/* Content */}
      <div
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        style={{ position: 'relative', zIndex: 2 }}
      >
        <h2
          style={{
            fontSize: '49px',
            fontFamily: 'var(--font-copernicus)',
            fontWeight: '400',
            color: '#071C26',
            letterSpacing: '-1px',
            marginBottom: '30px',
          }}
        >
          Stay Connected
        </h2>

        <p
          style={{
            fontSize: '15px',
            fontFamily: 'var(--font-sulsans-light)',
            fontWeight: '400',
            color: '#071C26',
            lineHeight: '22px',
            marginBottom: '20px',
          }}
        >
          Okkanti is built for the health providers who want to improve outcomes by making unbiased
          referrals to home and community-based care, for all pregnant, planning or parenting people
          who want to access the best care for their family, and for the birthworkers,
          healthworkers, and experts that make having a baby safer and more supported.
        </p>

        <p
          style={{
            fontSize: '15px',
            fontFamily: 'var(--font-sulsans-light)',
            fontWeight: '400',
            color: '#071C26',
            lineHeight: '22px',
            marginBottom: '40px',
          }}
        >
          Stay connected and find out how you can be served by the ecosystem of resources we&apos;ve
          built just for you.
        </p>

        {submitted ? (
          <div
            style={{
              padding: '40px',
              backgroundColor: 'rgba(206, 255, 164, 0.3)',
              borderRadius: '16px',
              marginTop: '30px',
            }}
          >
            <p
              style={{
                fontSize: '18px',
                fontFamily: 'var(--font-copernicus)',
                color: '#3a4e3d',
              }}
            >
              Thank you for subscribing! We&apos;ll be in touch soon.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-5 lg:bg-white lg:border lg:border-[rgb(156,164,168)] lg:rounded-[50px] lg:p-[20px_30px] max-w-[1200px] mx-auto"
          >
            {/* Email Input */}
            <div className="flex items-center bg-white rounded-[50px] px-6 py-4 border border-[rgb(156,164,168)] lg:flex-1 lg:bg-transparent lg:rounded-none lg:px-0 lg:py-0 lg:border-none">
              <label
                htmlFor="email-input"
                className="whitespace-nowrap mr-2 lg:mr-3"
                style={{
                  fontSize: '15px',
                  fontFamily: 'var(--font-sulsans-light)',
                  color: 'rgb(7, 28, 38)',
                }}
              >
                Email Address:
              </label>
              <input
                id="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 min-w-0"
                style={{
                  fontSize: '15px',
                  fontFamily: 'var(--font-sulsans-light)',
                  color: 'rgb(7, 28, 38)',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  padding: '0',
                }}
              />
            </div>

            {/* Divider - desktop only */}
            <div
              className="hidden lg:block"
              style={{
                width: '1px',
                height: '40px',
                backgroundColor: 'rgb(156, 164, 168)',
              }}
            />

            {/* Category Dropdown */}
            <div className="flex items-center bg-white rounded-[50px] px-6 py-4 border border-[rgb(156,164,168)] lg:flex-shrink-0 lg:bg-transparent lg:rounded-none lg:px-0 lg:py-0 lg:border-none">
              <label
                htmlFor="category-select"
                className="whitespace-nowrap mr-2 lg:mr-3"
                style={{
                  fontSize: '15px',
                  fontFamily: 'var(--font-sulsans-light)',
                  color: 'rgb(7, 28, 38)',
                }}
              >
                I am
              </label>
              <select
                id="category-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="flex-1"
                style={{
                  fontSize: '15px',
                  fontFamily: 'var(--font-sulsans-light)',
                  color: 'rgb(7, 28, 38)',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  padding: '0',
                  cursor: 'pointer',
                }}
              >
                <option value="seeking care">Seeking care</option>
                <option value="looking for trusted referral sources">
                  Looking for trusted referral sources
                </option>
                <option value="a birth or parenting expert">A birth or parenting expert</option>
              </select>
            </div>

            {/* Subscribe Button */}
            <button
              type="submit"
              className="w-full lg:w-auto lg:flex-shrink-0"
              style={{
                padding: '16px 40px',
                fontSize: '15px',
                fontFamily: 'var(--font-copernicus)',
                fontWeight: '400',
                backgroundColor: 'rgb(58, 78, 61)',
                color: 'white',
                borderRadius: '40px',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.3s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgb(47, 63, 49)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgb(58, 78, 61)'
              }}
            >
              Subscribe
            </button>
          </form>
        )}
      </div>
    </section>
  )
}
